/* ============
 * Company Store
 * ============
 */

import { defineStore } from 'pinia';
import './pinia';
import companyInfo from '../services/account/companyInfo';
import permissionService from '../services/permission';
import { useAccountStore } from './account';

const STORAGE_KEY = 'instead-of-customer';

// Sent as `X-Instead-Of-Customer` by the request interceptor (bootstrap.js).
const forgetActiveCompany = () => localStorage.removeItem(STORAGE_KEY);

// The main account is the default one: it is never sent as the active account.
const rememberActiveCompany = (companyId) => {
  if (companyId === useAccountStore().mainCompanyId) {
    forgetActiveCompany();
  } else {
    localStorage.setItem(STORAGE_KEY, companyId);
  }
};

/**
 * SP-14862 — depth-first lookup of a company in the account tree.
 *
 * The persisted selection (`instead-of-customer`) can point at any node of the
 * tree, not only a direct child of the account owner: the team switcher lets the
 * user drill down into nested teams (`pages/checkout/chooseTeam/recursiveTeamList`).
 * Scanning `children` one level deep lost the selection on reload for those nodes,
 * which in turn hid the sidebar counters.
 */
const findCompanyInTree = (company, companyId) => {
  const children = company.children || [];

  for (let i = 0; i < children.length; i += 1) {
    if (children[i].id === companyId) {
      return children[i];
    }

    const found = findCompanyInTree(children[i], companyId);

    if (found) {
      return found;
    }
  }

  return null;
};

export const useCompanyStore = defineStore('company', {
  state: () => ({
    id: null,
    parentCompany: null,
    childrenCompany: null,
    childrenActive: false,
    noChildren: true,
    kpiCompany: null,
    nameCompany: null,
    billingStatusCompany: null,
    quotesPending: null,
    paymentStatus: null,
    orderBlocked: false,
    tempAgencySwitchDate: null,
    activatedNewForm: null,
    tags: [],
    // SP-14823 — tags of the whole account tree, for the missions "Tags" filter and export.
    filterTags: [],
    currentCompany: null,
  }),

  actions: {
    setDefault(company) {
      const companyActive = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      const companyChildren = findCompanyInTree(company, companyActive);
      // `/me` is reloaded when switching to another company of the login (SP-14892)
      this.childrenActive = false;
      this.childrenCompany = null;
      this.noChildren = true;
      this.id = company.id;
      this.parentCompany = company;
      this.kpiCompany = company.kpi;
      this.paymentStatus = company.paymentStatus;
      this.orderBlocked = company.orderBlocked;
      this.tempAgencySwitchDate = company.tempAgencySwitchDate;
      this.activatedNewForm = company.activatedNewForm;
      this.tags = company.tags;
      this.filterTags = company.filterTags || company.tags;
      this.currentCompany = company;
      if (company.children.length > 0) {
        this.noChildren = false;
      }

      if (companyActive && companyChildren) {
        this.change(companyChildren);
      } else {
        // The stored account is the root itself (another company of the login, SP-14892),
        // or a link removed since: forget it, otherwise every call answers 401.
        if (companyActive && companyActive !== company.id) {
          forgetActiveCompany();
        }
        this.nameCompany = company.name;
        this.billingStatusCompany = company.billingStatus;
        this.quotesPending = company.countPendingQuote;
      }
    },

    rememberAccount(companyId) {
      rememberActiveCompany(companyId);
    },

    update(company) {
      this.nameCompany = company.name;
      this.parentCompany.name = company.name;
      this.parentCompany.logo = company.logo;
    },

    change(company) {
      companyInfo(company.id)
        .catch((error) => {
          // SP-14751 — refreshing the SEPA banner is informative only: a request aborted by
          // the navigation that follows a team switch must not bubble up as an unhandled
          // rejection (same GET /v3/customers/{id} as the /template page).
          window.console.warn('companyStore.change: company info request failed', error && error.message);
        });
      this.id = company.id;
      this.quotesPending = company.count_pending_quote;
      this.childrenCompany = company;
      this.childrenActive = true;
      this.noChildren = true;
      this.kpiCompany = company.kpi;
      this.nameCompany = company.name;
      this.paymentStatus = company.paymentStatus;
      this.orderBlocked = company.orderBlocked;
      this.tempAgencySwitchDate = company.temp_agency_switch_date;
      this.activatedNewForm = company.activated_new_form;
      this.billingStatusCompany = company.billing_status;
      this.tags = company.tags;
      this.filterTags = company.filter_tags || company.tags;
      this.currentCompany = company;
      if (company.children.length > 0) {
        this.noChildren = false;
      }
      rememberActiveCompany(company.id);
      this.refreshPermissions();
    },

    reset() {
      this.quotesPending = this.parentCompany.countPendingQuote;
      this.childrenActive = false;
      this.childrenCompany = null;
      this.kpiCompany = this.parentCompany.kpi;
      this.nameCompany = this.parentCompany.name;
      this.paymentStatus = this.parentCompany.paymentStatus;
      this.orderBlocked = this.parentCompany.orderBlocked;
      this.tempAgencySwitchDate = this.parentCompany.tempAgencySwitchDate;
      this.activatedNewForm = this.parentCompany.activatedNewForm;
      this.billingStatusCompany = this.parentCompany.billingStatus;
      this.currentCompany = this.parentCompany;
      if (this.parentCompany.children.length > 0) {
        this.noChildren = false;
      }

      // "All accounts" of another company of the login stays on that company (SP-14892)
      rememberActiveCompany(this.parentCompany.id);
      this.refreshPermissions();
      companyInfo(this.parentCompany.id)
        .catch((error) => {
          // SP-14751 — same non-blocking treatment as change() above.
          window.console.warn('companyStore.reset: company info request failed', error && error.message);
        });
    },

    refreshPermissions() {
      permissionService.all()
        .catch((error) => {
          window.console.warn('companyStore: permissions request failed', error && error.message);
        });
    },

    updateCompanyStats(number) {
      this.kpiCompany.count_jobs_pending = number;
    },

    updateJobsPending(number) {
      this.kpiCompany.count_jobs_pending = number;
    },
  },
});

export default useCompanyStore;
